// İnternet / sync əlaqə göstəricisi — Header-də.
//
// İstifadəçi (POS işçisi) internetə bağlı olub-olmadığını bir baxışda bilsin:
//   • Qoşulu  → yaşıl Wifi ikonu.
//   • Kəsik   → qırmızı WifiOff (üstündən xətt) — "internet gedib, sync işləməyəcək".
//
// Necə yoxlanır: birbaşa cloud-a (`base_url`) `no-cors` ping atılır. `no-cors`
// opaque cavab qaytarır — server əlçatandırsa resolve olur (status fərq etməz),
// şəbəkə kəsikdirsə reject olur. Beləliklə `navigator.onLine`-dan (router-ə bağlı
// qalanda yanlış "online" göstərir) və pos_local breaker-indən (yavaş açılır) daha
// dəqiq və sürətli siqnal alırıq. Əlavə olaraq brauzerin offline event-i ani reaksiya verir.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Wifi, WifiOff } from "lucide-react";

import { useTheme } from "../context/ThemeContext";
import { useI18n } from "../i18n/I18nContext";
import { base_url } from "../api/index";

const POLL_MS = 8000;
const FETCH_TIMEOUT_MS = 4000;

const ConnectivityIndicator: React.FC = () => {
  const { isDark } = useTheme();
  const { t } = useI18n();
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  // Dil dəyişəndə mətn yenilənsin deyə açar saxlanır (mətn deyil).
  const [detailKey, setDetailKey] = useState<string>("");
  const mounted = useRef(true);

  const check = useCallback(async () => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      if (mounted.current) {
        setOnline(false);
        setDetailKey("conn.deviceOffline");
      }
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      // no-cors: opaque cavab — server əlçatandırsa (status nə olursa olsun) resolve.
      await fetch(`${base_url}/`, {
        mode: "no-cors",
        cache: "no-store",
        signal: controller.signal,
      });
      if (!mounted.current) return;
      setOnline(true);
      setDetailKey("conn.online");
    } catch {
      if (!mounted.current) return;
      setOnline(false);
      setDetailKey("conn.offline");
    } finally {
      clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;

    const onBrowserOnline = () => {
      // Şəbəkə qayıdanda dərhal real yoxlama et.
      void check();
    };
    const onBrowserOffline = () => {
      if (!mounted.current) return;
      setOnline(false);
      setDetailKey("conn.deviceOffline");
    };
    window.addEventListener("online", onBrowserOnline);
    window.addEventListener("offline", onBrowserOffline);

    void check();
    const id = setInterval(() => void check(), POLL_MS);

    return () => {
      mounted.current = false;
      clearInterval(id);
      window.removeEventListener("online", onBrowserOnline);
      window.removeEventListener("offline", onBrowserOffline);
    };
  }, [check]);

  const title = detailKey
    ? t(detailKey)
    : online
      ? t("conn.online.short")
      : t("conn.offline");

  return (
    <div
      title={title}
      aria-label={title}
      className={`p-2.5 rounded-xl transition-all flex items-center justify-center ${
        online
          ? isDark
            ? "bg-green-500/15 text-green-400"
            : "bg-green-50 text-green-600"
          : isDark
            ? "bg-red-500/15 text-red-400"
            : "bg-red-50 text-red-500"
      }`}
    >
      {online ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
    </div>
  );
};

export default ConnectivityIndicator;
